import React, { useState, useCallback, useRef } from 'react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import { MoreHorizontal, PlusCircle } from 'lucide-react';
import { Badge } from 'src/components/ui/badge';
import { Button } from 'src/components/ui/button';
import { DataTable } from 'src/components/common/DataTable';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';

// Cell Components
const FileNameCell = ({ value }) => <div className="text-left">{value}</div>;

const StatusCell = () => (
  <div className="text-left">
    <Badge variant="Outline" className="text-red-500 border-red-500">
      Duplicate
    </Badge>
  </div>
);

const ActionsCell = ({ row }) => {
  const file = row.original;

  return (
    <div className="text-left">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" className="h-8 w-8 p-0">
            <span className="sr-only">Open menu</span>
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuLabel>Actions</DropdownMenuLabel>
          <DropdownMenuItem
            onClick={() => navigator.clipboard.writeText(file.name)}
          >
            Copy filename
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem>Edit file</DropdownMenuItem>
          <DropdownMenuItem>Delete file</DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

// Table Columns Configuration
const getTableColumns = () => [
  {
    accessorKey: 'name',
    header: 'Filename',
    cell: ({ row }) => <FileNameCell value={row.getValue('name')} />,
  },
  {
    accessorKey: 'status',
    header: 'Status',
    cell: StatusCell,
  },
  {
    id: 'actions',
    header: 'Actions',
    cell: ActionsCell,
  },
];

function UploadResume() {
  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleFiles = useCallback((newFiles) => {
    setFiles((prevFiles) => [...prevFiles, ...Array.from(newFiles)]);
  }, []);

  const onClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileInputChange = useCallback(
    (event) => {
      handleFiles(event.target.files);
    },
    [handleFiles]
  );

  const columns = getTableColumns();

  return (
    <div className="w-full">
      <PageHeader title="Upload Resume" />
      <PageContainer className="flex justify-start">
        <Button
          variant="outline"
          onClick={onClick}
          className="h-8 border-dashed border-[hsl(var(--lightblue))] text-[hsl(var(--ahedgeblue))]"
          disabled
        >
          <PlusCircle className="mr-2 h-4 w-4" />
          Upload Resume
        </Button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={onFileInputChange}
          multiple
          className="hidden"
          accept=".pdf,.doc,.docx"
        />
      </PageContainer>

      {files.length > 0 && (
        <div className="mt-4 w-full">
          <DataTable
            columns={columns}
            data={files}
            filterColumn="name"
            showSearch
            showPagination
            itemsPerPageOptions={[5, 10, 25, 50]}
            defaultItemsPerPage={10}
          />
        </div>
      )}
    </div>
  );
}

export default UploadResume;
